import { clsx } from 'clsx';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { formatDate } from '@/shared/utils/format';
import type { PublicReferralView, ReferralDetail, ToothDef } from '../api/referralsApi';
import { ToothChart } from './ToothChart';
import { BodyRegionPicker } from './BodyRegionPicker';
import { AttachmentDropzone } from './AttachmentDropzone';
import { RecipientStatusPill, UrgencyPill } from './ReferralStatusPill';
import styles from './ReferralSlipView.module.css';

type Slip = ReferralDetail | PublicReferralView['referral'];

interface ReferralSlipViewProps {
  referral: Slip;
  /** Chart definitions; when absent the teeth are listed as text only. */
  teeth?: ToothDef[];
  bodyRegions?: Array<{ key: string; label: string; group: string; sided: boolean }>;
  numbering?: 'universal' | 'fdi';
  /** Which side is reading: decides what "the other office" means in headings. */
  viewer: 'sender' | 'recipient';
  attachmentsSlot?: React.ReactNode;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.fieldBlock}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  );
}

/** The slip as the receiving office reads it: patient, reason, chart, note, files. */
export function ReferralSlipView({ referral: r, teeth, bodyRegions, numbering = 'universal', viewer, attachmentsSlot }: ReferralSlipViewProps) {
  const details = r.patientDetails ?? {};
  const hasDetails = Boolean(details.insurance || details.memberId || details.preferredLanguage || details.address || details.notes || r.patientDob || r.patientEmail);
  return (
    <div className={styles.slip}>
      {(r.aiSummary || (r.aiTriage && r.aiTriage.flags.length > 0)) && (
        <div className={clsx(styles.ai, r.aiTriage?.urgency === 'urgent' && styles.aiUrgent)}>
          <div className={styles.aiHead}>
            <Sparkles size={14} />
            At a glance
            {r.aiTriage && <UrgencyPill urgency={r.aiTriage.urgency} prefix="Reads as: " />}
          </div>
          {r.aiSummary && <p className={styles.aiSummary}>{r.aiSummary}</p>}
          {r.aiTriage && r.aiTriage.flags.length > 0 && (
            <ul className={styles.flags}>
              {r.aiTriage.flags.map((f) => (
                <li key={f}>
                  <AlertTriangle size={12} /> {f}
                </li>
              ))}
            </ul>
          )}
          {r.aiTriage?.nextStep && viewer === 'recipient' && <p className={styles.nextStep}>{r.aiTriage.nextStep}</p>}
        </div>
      )}

      <div className={styles.grid}>
        <div className={styles.col}>
          <h4 className={styles.sectionTitle}>Patient information</h4>
          <div className={styles.twoCol}>
            <Field label="Name">
              {r.patientLastName}, {r.patientFirstName}
            </Field>
            <Field label="Phone">{r.patientPhone ?? r.patientPhoneMasked ?? '—'}</Field>
            {r.patientDob && <Field label="Date of birth">{formatDate(r.patientDob)}</Field>}
            {r.patientEmail && <Field label="Email">{r.patientEmail}</Field>}
            {details.insurance && (
              <Field label="Insurance">
                {details.insurance}
                {details.memberId ? ` · ${details.memberId}` : ''}
              </Field>
            )}
            {details.preferredLanguage && <Field label="Preferred language">{details.preferredLanguage}</Field>}
            {details.address && <Field label="Address">{details.address}</Field>}
          </div>
          {details.notes && <Field label="Patient notes">{details.notes}</Field>}
          {!hasDetails && null}

          <h4 className={styles.sectionTitle}>Reason for referral</h4>
          <div className={styles.reasonRow}>
            {r.reasons.length ? r.reasons.join(', ') : <span className={styles.muted}>Not specified</span>}
            <UrgencyPill urgency={r.urgency} />
          </div>

          {r.comment && (
            <>
              <h4 className={styles.sectionTitle}>Comment</h4>
              <p className={styles.comment}>{r.comment}</p>
            </>
          )}

          <h4 className={styles.sectionTitle}>
            {viewer === 'recipient' ? 'Referring office' : r.recipients.length > 1 ? `Offered to ${r.recipients.length} offices` : 'Sent to'}
          </h4>
          {viewer === 'recipient' ? (
            <div className={styles.twoCol}>
              <Field label="Practice">{r.senderPracticeName}</Field>
              {r.referringDoctorName && <Field label="Referring doctor">{r.referringDoctorName}</Field>}
              {r.senderPhone && <Field label="Phone">{r.senderPhone}</Field>}
            </div>
          ) : r.recipients.length > 1 ? (
            <ol className={styles.officeList}>
              {r.recipients.map((o) => (
                <li key={o.id} className={clsx(styles.office, o.isChosen && styles.officeChosen)}>
                  <div className={styles.officeHead}>
                    <strong>{o.practice.name}</strong>
                    {r.status !== 'draft' && <RecipientStatusPill status={o.status} />}
                  </div>
                  <div className={styles.muted}>
                    {[o.practice.specialtyLabel, o.preferredProviderName ? `Ask for ${o.preferredProviderName}` : null, o.practice.phone, o.practice.email]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </li>
              ))}
            </ol>
          ) : r.recipient ? (
            <div className={styles.twoCol}>
              <Field label="Practice">{r.recipient.name}</Field>
              {r.recipient.specialtyLabel && <Field label="Specialty">{r.recipient.specialtyLabel}</Field>}
              {r.preferredProviderName && <Field label="Preferred doctor">{r.preferredProviderName}</Field>}
              {r.recipient.phone && <Field label="Phone">{r.recipient.phone}</Field>}
              {r.recipient.email && <Field label="Email">{r.recipient.email}</Field>}
            </div>
          ) : (
            <p className={styles.muted}>No receiving office chosen yet.</p>
          )}
        </div>

        <div className={styles.col}>
          <h4 className={styles.sectionTitle}>{r.vertical === 'dental' ? 'Teeth' : 'Affected area'}</h4>
          {r.vertical === 'dental' ? (
            teeth ? (
              <ToothChart teeth={teeth} selected={r.teeth} readOnly numbering={numbering} compact />
            ) : (
              <p>{r.teethLabel || <span className={styles.muted}>None noted</span>}</p>
            )
          ) : bodyRegions ? (
            <BodyRegionPicker regions={bodyRegions} selected={r.bodyRegions} readOnly />
          ) : (
            <p>{r.bodyRegionsLabel || <span className={styles.muted}>None noted</span>}</p>
          )}
          {r.vertical === 'dental' && r.teeth.length > 0 && <p className={styles.teethLabel}>{r.teethLabel}</p>}
        </div>
      </div>

      <h4 className={styles.sectionTitle}>Additional documents</h4>
      {attachmentsSlot ?? <AttachmentDropzone attachments={r.attachments} disabled disabledReason={r.attachments.length ? null : 'No files attached.'} />}

      {r.reports.length > 0 && (
        <>
          <h4 className={styles.sectionTitle}>Reports back</h4>
          <div className={styles.reports}>
            {r.reports.map((rep) => (
              <div key={rep.id} className={styles.report}>
                <div className={styles.reportHead}>
                  <strong>{rep.authorName}</strong>
                  <span className={styles.muted}>{formatDate(rep.createdAt, 'MMM d, yyyy h:mm a')}</span>
                </div>
                {rep.procedures.length > 0 && <div className={styles.reportProcedures}>{rep.procedures.join(', ')}</div>}
                <p className={styles.reportBody}>{rep.body}</p>
                {rep.outcome && (
                  <p className={styles.reportLine}>
                    <strong>Outcome:</strong> {rep.outcome}
                  </p>
                )}
                {rep.followUp && (
                  <p className={styles.reportLine}>
                    <strong>Follow-up:</strong> {rep.followUp}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
